import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SchemaOrg from '@/components/base/SchemaOrg';
import { aboutInfo } from '@/mocks/movieData';
import { useState } from 'react';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypot = formData.get('phone_alt');
    if (honeypot && String(honeypot).trim() !== '') {
      setFormStatus('success');
      setFormMessage('消息已发送，我们会尽快回复您！');
      return;
    }
    formData.delete('phone_alt');

    try {
      const response = await fetch('https://readdy.ai/api/form/d9nfspcit35ii3jdlqt0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed: Record<string, unknown> = {};
      try { parsed = JSON.parse(responseText); } catch { /* ignore */ }

      if (response.ok && parsed.code === 'OK') {
        setFormStatus('success');
        setFormMessage('感谢您的留言，我们会尽快回复您！');
        form.reset();
      } else {
        const serverMsg = (parsed?.meta as Record<string, unknown>)?.message || (parsed?.message as string) || responseText || '提交失败，请稍后重试';
        if (typeof serverMsg === 'string' && (serverMsg.includes('spam') || serverMsg.includes('form data is spam'))) {
          setFormStatus('error');
          setFormMessage('提交失败，请稍后重试');
        } else {
          setFormStatus('error');
          setFormMessage(typeof serverMsg === 'string' ? serverMsg : '提交失败，请稍后重试');
        }
      }
    } catch {
      setFormStatus('error');
      setFormMessage('网络错误，请检查连接后重试');
    }
  };
  return (
    <>
      <ScrollToTop />
      <SchemaOrg type="Organization" data={{
        name: aboutInfo.brandName,
        url: 'https://jiujiuys.com',
        description: aboutInfo.introduction,
        contactPoint: {
          '@type': 'ContactPoint',
          telephone: `+86-${aboutInfo.contact.phone}`,
          contactType: 'customer service',
          availableLanguage: 'Chinese',
        },
        address: {
          '@type': 'PostalAddress',
          streetAddress: aboutInfo.contact.address,
          addressCountry: 'CN',
        },
      }} />
      <SchemaOrg type="WebPage" data={{
        name: '关于我们 - 久久影视',
        description: aboutInfo.introduction,
        inLanguage: 'zh-CN',
      }} />

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="pt-16">
          <Breadcrumb />

          <section className="w-full px-4 md:px-6 py-10 md:py-16 bg-background-50">
            <div className="max-w-[1000px] mx-auto text-center">
              <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-900 mb-3">关于久久影视</h1>
              <p className="text-foreground-500 text-sm md:text-base max-w-2xl mx-auto leading-relaxed">
                {aboutInfo.slogan}
              </p>
            </div>
          </section>

          <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-100">
            <div className="max-w-[1000px] mx-auto grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
              <div>
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-4">品牌故事</h2>
                <p className="text-foreground-700 leading-relaxed text-sm md:text-base mb-4">
                  {aboutInfo.introduction}
                </p>
                <p className="text-foreground-600 leading-relaxed text-sm">
                  {aboutInfo.story}
                </p>
              </div>
              <div className="rounded-lg overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Modern%20Chinese%20office%20space%20with%20warm%20lighting%2C%20creative%20team%20discussion%2C%20minimalist%20design%20with%20wooden%20accents%2C%20large%20windows%20with%20natural%20light%2C%20collaborative%20workspace%2C%20cinematic%20warm%20tones&width=800&height=600&seq=about-brand&orientation=landscape"
                  alt="久久影视团队"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </section>

          <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-50">
            <div className="max-w-[1000px] mx-auto">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 text-center mb-8">我们的数据</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
                {aboutInfo.stats.map((stat) => (
                  <div key={stat.label} className="text-center p-5 md:p-6 rounded-lg bg-background-100">
                    <div className="text-2xl md:text-3xl font-heading font-bold text-primary-500 mb-1">{stat.value}</div>
                    <div className="text-xs md:text-sm text-foreground-500">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-100">
            <div className="max-w-[1000px] mx-auto">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 text-center mb-8">核心价值观</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-5">
                {aboutInfo.values.map((value) => (
                  <div key={value.title} className="p-5 md:p-6 rounded-lg bg-background-50 border border-background-200/70">
                    <h3 className="text-base md:text-lg font-heading font-semibold text-foreground-900 mb-2">{value.title}</h3>
                    <p className="text-sm text-foreground-600 leading-relaxed">{value.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-50">
            <div className="max-w-[800px] mx-auto">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 text-center mb-8">联系我们</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="flex flex-col gap-4">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 shrink-0">
                      <i className="ri-mail-line text-lg"></i>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-foreground-900">电子邮件</div>
                      <div className="text-sm text-foreground-500">{aboutInfo.contact.email}</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 shrink-0">
                      <i className="ri-phone-line text-lg"></i>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-foreground-900">客服电话</div>
                      <div className="text-sm text-foreground-500">{aboutInfo.contact.phone}</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 shrink-0">
                      <i className="ri-map-pin-line text-lg"></i>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-foreground-900">公司地址</div>
                      <div className="text-sm text-foreground-500">{aboutInfo.contact.address}</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 shrink-0">
                      <i className="ri-time-line text-lg"></i>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-foreground-900">工作时间</div>
                      <div className="text-sm text-foreground-500">{aboutInfo.contact.workingHours}</div>
                    </div>
                  </div>
                </div>

                <div className="bg-background-100 rounded-lg p-5 md:p-6">
                  <h3 className="text-base font-heading font-semibold text-foreground-900 mb-4">给我们留言</h3>
                  <form data-readdy-form="" className="flex flex-col gap-3" onSubmit={handleSubmit}>
                    <div>
                      <input
                        type="text"
                        name="name"
                        placeholder="您的姓名"
                        className="w-full px-3 py-2.5 rounded-lg bg-background-50 border border-background-200/70 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                      />
                    </div>
                    <div>
                      <input
                        type="email"
                        name="email"
                        placeholder="您的邮箱"
                        className="w-full px-3 py-2.5 rounded-lg bg-background-50 border border-background-200/70 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                      />
                    </div>
                    <div>
                      <textarea
                        name="message"
                        placeholder="请输入您想说的话..."
                        rows={4}
                        maxLength={500}
                        className="w-full px-3 py-2.5 rounded-lg bg-background-50 border border-background-200/70 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                      ></textarea>
                    </div>
                    <div>
                      <input
                        type="text"
                        name="phone_alt"
                        tabIndex={-1}
                        autoComplete="off"
                        aria-hidden="true"
                        readOnly
                        className="honeypot-field"
                      />
                    </div>
                    <button
                      type="submit"
                      className="w-full py-3 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-lg text-sm font-medium whitespace-nowrap transition-colors cursor-pointer"
                    >
                      发送消息
                    </button>
                    {formStatus !== 'idle' && (
                      <p className={`text-sm text-center ${formStatus === 'success' ? 'text-green-600' : 'text-red-500'}`}>
                        {formStatus === 'success' ? (
                          <><i className="ri-checkbox-circle-fill mr-1"></i>{formMessage}</>
                        ) : (
                          <><i className="ri-error-warning-fill mr-1"></i>{formMessage}</>
                        )}
                      </p>
                    )}
                  </form>
                </div>
              </div>
            </div>
          </section>

          <section id="faq" className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-100">
            <div className="max-w-[800px] mx-auto">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 text-center mb-8">用户口碑</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
                {[
                  { name: '追剧达人小王', text: '久久影视的更新速度真的太快了！新剧上线第一时间就能看到，画质也特别清晰，追剧体验满分。', rating: 5 },
                  { name: '电影爱好者老李', text: '一直在用久久影视看最新电影上映信息，分类很清晰，找片特别方便。推荐给所有影迷朋友！', rating: 5 },
                  { name: '综艺迷小张', text: '每周的综艺更新从不落下，高清流畅不缓冲，久久影视是我用过最好的影视平台。', rating: 5 },
                ].map((review) => (
                  <div key={review.name} className="p-5 rounded-lg bg-background-50 border border-background-200/70">
                    <div className="flex items-center gap-1 mb-2">
                      {Array.from({ length: review.rating }).map((_, i) => (
                        <i key={i} className="ri-star-fill text-accent-500 text-xs"></i>
                      ))}
                    </div>
                    <p className="text-sm text-foreground-700 leading-relaxed mb-3">&ldquo;{review.text}&rdquo;</p>
                    <div className="text-xs font-medium text-foreground-900">{review.name}</div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        </div>
        <Footer />
      </div>
    </>
  );
}